import { useEffect, useMemo, useRef, useState } from 'react'
import { fmtDisplayDateTime } from '@utils/displayDate'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useBackNav from '@hooks/useBackNav'
import useFirmName from '@hooks/useFirmName'
import { REPORTS_LIST_PATH } from '@config/reportRoutes'
import ReportAiSummary from './ReportAiSummary'
import { aiScopeLine } from './aiScope'
import ReportExport from './ReportExport'
import { clientExportRefusal } from './clientExportRefusal'
import ReportActionBar from './ReportActionBar'

/* A fixed id, not useId(): the Bar 1 trigger and the panel it controls are far
   apart in the tree and only one of each exists per page — the same reasoning as
   ReportView.jsx:140, and the same constant, so both hosts behave identically. */
const AI_PANEL_ID = 'report-ai-panel'
import { ReportRunStateContext } from './reportRunStateContext'
import SavedResultBanner from './SavedResultBanner'
import RunningReportBand from './queue/RunningReportBand'
import { AI_REPORT_SUMMARY } from '@config/featureFlags'
import { useAiMode } from '@hooks/useUserSettings'
import { userFacingError } from '@utils/reportProgress'
import usePreferencesStore from '@store/preferencesStore'
import { ReportExportViewContext } from './reportExportViewContext'
import { useColumnAliasContext } from './columnAliasContext'
import ColumnAliasEditor from './ColumnAliasEditor'
import { exportFileName } from './reportGridUtils'
import { specializedJobsOn } from '@api/v1/reportJobRun'
import { trackRunStarted, trackRunCompleted, trackRunFailed, trackResultRendered } from '@/analytics/pendo'
/*
 * NOT from './analytics/reportFacts' — that imports pages/reports/registry,
 * which statically imports this component's own callers, and the cycle left
 * REPORT_RENDERERS.cbARReportView undefined. errorClass.js is a leaf.
 */
import { errorClass } from '@/analytics/errorClass'
import { PENDO_IGNORE } from '@/analytics/domPrivacy'

/**
 * Shared chrome for specialized reports: header (back arrow, title,
 * export buttons), an error pane, a loading state, and a slot for the
 * filter bar + result grid.
 *
 * Caller passes a `download(format, getAccessToken, fallback)` so each
 * report decides whether to use the legacy export pipeline or its own.
 */
/**
 * What a document says about its criteria when the run on screen is a reopened
 * snapshot whose own criteria could not be recovered (CC-53363).
 *
 * Written as a phrase that completes the sentence each surface already builds, so
 * one constant serves all of them rather than three near-copies:
 *   `Filters — not recorded for this saved run`   (CSV / XLSX / ReportExport)
 *   `Scope: not recorded for this saved run · 1,208 rows`   (AI panel)
 *
 * Deliberately NOT the empty string. `describeFilters` returns '' for "this report
 * filters by nothing", which is a true statement; this is a different claim and
 * must not collapse into it. And deliberately not omitted — an export header with
 * no filters line reads as "no filters were applied".
 */
export const SAVED_RUN_CRITERIA_UNKNOWN = 'not recorded for this saved run'

const SpecializedReportShell = ({
  name,
  summary,
  running,
  error,
  filterBar,
  children,
  exportFormats = ['csv', 'xlsx'],
  download, // (format, getAccessToken) => Promise
  /*
   * The page's LIVE 'Filters applied' line, a useMemo over its own filter state
   * on all 20 pages. Never rendered straight into a document once a run has
   * committed — read `documentFiltersSummary` below, which is `ranCriteria`'s copy
   * of this line, taken when the run whose rows are on screen STARTED.
   */
  filtersSummary,
  /*
   * Spec 131 — `{ filtersSummary, printSubtitle }` of the run whose rows are ON
   * SCREEN, i.e. `useLatestRun().ran?.criteria`. The page captures it when the
   * run starts and publishes it in the same commit as the rows; `null` before any
   * run has committed (the documents then fall back to the live props, as they
   * always have). A page that omits it gets the live props on every document —
   * safe-looking and WRONG mid-run — so all 20 pass it and each has a test.
   */
  ranCriteria,
  /*
   * Spec 131 — the ticket of the run most recently STARTED
   * (`useLatestRun().runKey`). Published on ReportRunStateContext for
   * SpecializedFilterBar's stale-since-reset band, and it re-seeds the running
   * band's clock: a superseding run keeps `running` true, so neither can key on
   * the running edge any more.
   */
  runKey,
  summaryData, // { headers, rows } — enables the AI Summary button when provided
  savedAsOf, // ms timestamp — when set, this is a saved result reopened from the tray
  /*
   * CC-53363 — the criteria a REOPENED saved run was actually produced under.
   * THREE STATES, and the difference between them matters on a financial document:
   *
   *   undefined  the page has not spoken. On a reopen the shell then refuses to
   *              print the page's own filters (see documentFiltersSummary below),
   *              because those are the DEFAULTS, not the run's.
   *   a string   the run's own criteria, reconstructed by the page from the job's
   *              savedParams. Wins over everything.
   *   null       the page looked and could not reconstruct them. Same outcome as
   *              undefined, but says so deliberately rather than by omission.
   *
   * Spec 185 (CC-53435): nine pages now pass it on a reopen — P&L / Comparative
   * P&L, Balance Sheet, Trial Balance, General Ledger, Aged A/R, Fee Receipts,
   * Timekeeper Analysis, Time Entries and Monthly Productivity — each built by
   * `savedRunCriteria.js` from the JOB in hand, so it is a string for a job whose
   * params carry the criteria and null for one that does not (the same report id
   * stores both shapes). The other eleven pages have no captured job payload and
   * leave it undefined: the constant is their answer, a result rather than a gap
   * (spec 185 §5). P&L also passes it on a LIVE comparison run (spec 127 §4.3).
   */
  savedRunFiltersSummary,
  // ---- CC-50013 G6: the seven things Aged A/R needed that this shell did not
  // offer. Every one is optional and defaults to the behaviour the 19 existing
  // pages already get, so bringing AR in changes nothing for them. AR was the
  // last report outside this shell, and staying outside is what made CC-50010
  // and CC-50004 look like regressions of already-released fixes.
  /*
   * Extra print-masthead line under the firm name (AR: "As of …"). Also live,
   * also filter-derived, and on a point-in-time statement it IS the period — so
   * it is frozen with the rest (`documentPrintSubtitle`).
   */
  printSubtitle,
  progress, // { loaded, total } — render a determinate bar instead of a bare spinner
  getView, // override the published-view getter (AR keeps its own ref + fallback)
  /*
   * Captured WITHOUT a default so the analytics below can tell "this page did
   * not report a count" from "this report returned no rows". Only 1 of the 20
   * pages passes it, so a `= 0` default would have reported `0 empty` — a
   * measurement — for the other 19. ReportExport still gets 0.
   */
  rowCount: rowCountProp,
  hasGroups: hasGroupsProp, // override when the page knows its grouping up front
  ready = true, // false = a run has not produced a result yet, so hide the toolbar
  /*
   * Spec 209 §3: the report name analytics may see, when it must not be the report's own.
   * A firm-authored Advanced (Exago) report reaches Pendo as "Advanced Report" — in the
   * run and export events AND the document title, which the Pendo agent records with each
   * page view. The heading still shows the real name. Defaults to `name`, so every other
   * page sends exactly what it always did.
   */
  analyticsName,
  /*
   * Spec 214 final review: the error band may carry the firm's own words even where the
   * NAME is not private — the Advanced Report route's `/info` refusal (422
   * EXAGO_PROMPT_UNSTATED) quotes a firm-written prompt label under the generic name.
   * True gives the band PENDO_IGNORE, as `privateName` does. False on every other page,
   * which therefore renders exactly as before.
   */
  privateError = false,
}) => {
  useDocumentTitle(analyticsName ?? name)
  /*
   * Spec 209 §3: a page whose analytics name differs from the name it SHOWS is showing a
   * firm-authored name (a generic Advanced Report), so every element here that prints it, or
   * a sentence that may carry the firm's own words (a 422's prompt label in the error band),
   * carries PENDO_IGNORE: the heading row (ReportActionBar draws the h1 and the summary, so
   * the class wraps it), the running band and the error band. False on every other page,
   * which therefore renders exactly as before.
   */
  const privateName = analyticsName != null && analyticsName !== name
  const navigate = useNavigate()
  const rowCount = rowCountProp ?? 0
  // CC-50291: per-firm column names. `columns` is published UP by whichever
  // shared grid this page renders — a specialized report's columns exist only
  // inside its grid, so this is the only way the editor can list real ones.
  const aliasCtx = useColumnAliasContext()
  const [renamingColumns, setRenamingColumns] = useState(false)
  // Collapsed by default, exactly as on the generic engine (ReportView.jsx:309).
  const [aiCollapsed, setAiCollapsed] = useState(true)
  const canRenameColumns = !!aliasCtx?.canManage && (aliasCtx?.columns?.length ?? 0) > 0
  // CC-49903: Back preserves the selected Reports category (history-first).
  // CC-49980: the cold-load fallback names the reports LIST, not bare /reports
  // (which renders Dashboards once that beta is on).
  const goBack = useBackNav(REPORTS_LIST_PATH)
  // CC-49981: masthead + PDF carry the firm's real name from system settings.
  const firmName = useFirmName()
  const { getAccessTokenSilently, user } = useAuth0()
  const aiMode = useAiMode() // 'off' | 'summary' | 'chat'
  // CC-49844: the same persisted "Data only" choice ReportExport reads, so the
  // PRINT masthead below honors it as well as the export writers do.
  const dataOnly = !!usePreferencesStore().getExportDataOnly?.()

  /*
   * ── spec 094: run events for the 21 specialized reports ───────────────────
   *
   * Before this, specialized runs were counted by NOBODY on prod:
   * `ReportView.runReportWith` is the only other run seam and a specialized
   * report never enters it (the auto-run is gated on `!hasSpecialized`), while
   * cb-api only reports them when VITE_REPORT_JOBS_SPECIALIZED is on — true on
   * QA, false on prod. This shell is the one place all 20 pages pass through,
   * so instrumenting it here covers them without touching 20 files.
   *
   * The seam is the `running` prop transition, because the PAGES own their
   * fetch and this component never sees it. That is observation, not
   * interception, so each inference below is deliberately conservative:
   *
   *  - `error` arrives as a STRING, not an error object: every page's
   *    `errorMessage` builds one (`err?.response?.data?.error || err.message ||
   *    '…'`). Since spec 131 the page's useLatestRun sets it in its `catch` and
   *    `running=false` in its `finally`; React batches the two into one commit,
   *    so this effect still sees both together. errorClass accepts a string.
   *  - Spec 131: a chain of runs that supersede one another keeps `running`
   *    TRUE throughout (only the newest may turn it off), so a chain is ONE
   *    started/terminal pair here, stamped with the newest run's outcome. Before,
   *    the first run to land flipped `running` early and the pairs were garbled.
   *  - IDENTITY comes from the route (`useParams`), because the shell has no
   *    reportId prop and adding one would mean editing 20 pages. `actionKey` is
   *    genuinely unavailable here and is omitted rather than faked; reportId
   *    identifies the row on its own.
   *  - FILTERS are not visible here at all. `filtersSummary` is prose built
   *    from VALUES, so it must never be sent, and the shell never receives the
   *    filter object. filterCount/filtersUsed are therefore omitted — absent,
   *    not zero (see runBase).
   *  - `resultShape` is omitted for the same reason cb-api omits it: the only
   *    signals here (`hasGroups`, `hasExportView`) are set by the grid's OWN
   *    publish effect, which lands AFTER this transition, so reading them here
   *    would report a stale value. Absent beats guessed. For these 21 reports
   *    shape is a catalog fact anyway.
   */
  const [searchParams] = useSearchParams()
  const routeParams = useParams()
  const reopenJobId = searchParams.get('jobId')
  const runSeqRef = useRef(0)
  const runStartedAtRef = useRef(null)
  const runCountedRef = useRef(false)
  const wasRunningRef = useRef(false)

  /*
   * When the current run started, for the running band's elapsed clock.
   *
   * State rather than a ref because the band READS it during render: a ref set
   * in an effect is still null on the render that mounts the band, so the clock
   * would seed at 0 and — because the band's interval is gated on a non-null
   * start — never tick at all.
   *
   * And deliberately NOT reusing `runStartedAtRef` (:179), which looks like
   * exactly this value and is not. That ref is set inside the analytics branch
   * BELOW an early return for a `?jobId=` reopen, so it stays null for a reopen
   * and would give the band a dead clock on precisely the path where a user is
   * most likely to be waiting on a fetch. Two similar-looking values with
   * different lifetimes is worth the extra line.
   *
   * One state update per run transition, not per tick — the ticker lives inside
   * the band, so nothing here re-renders on the clock's account.
   *
   * Spec 131: also per `runKey`. A run that supersedes one still in flight keeps
   * `running` true, so without it the clock would time the whole chain instead
   * of the run the panel now describes — ReportView re-seeds per run the same way.
   */
  const [runStartedAt, setRunStartedAt] = useState(null)
  useEffect(() => {
    setRunStartedAt(running ? Date.now() : null)
  }, [running, runKey])

  useEffect(() => {
    const wasRunning = wasRunningRef.current
    wasRunningRef.current = !!running
    // StrictMode invokes this twice on mount; by the second pass the ref
    // already equals `running`, so no transition is seen and nothing re-fires.
    if (wasRunning === !!running) return undefined

    const reportId = Number.parseInt(routeParams.reportId, 10)
    if (!Number.isFinite(reportId)) return undefined

    if (running) {
      runSeqRef.current += 1
      /*
       * A `?jobId=` reopen is a READ of a stored snapshot, not a run, and these
       * pages start with `running: !!jobId` so it looks exactly like one.
       * Suppress only the FIRST cycle: the param stays in the URL afterwards,
       * so keying on its mere presence would silence every genuine run the user
       * then makes on that page.
       *
       * No specialized page reads `?rerun=1` today, so a reopen here never
       * re-runs — worth knowing if "Run again, same filters" is ever wired for
       * these reports, because then the first cycle WOULD be a real run.
       */
      runCountedRef.current = !(runSeqRef.current === 1 && reopenJobId)
      if (!runCountedRef.current) return undefined

      runStartedAtRef.current = Date.now()
      trackRunStarted({
        reportId,
        reportName: analyticsName ?? name,
        reportSurface: 'specialized',
        runMode: specializedJobsOn() ? 'job' : 'sync',
        handoff: false,
        // Honest rather than guessed: nothing else re-triggers a specialized
        // fetch, so the first cycle IS the page loading and every later one can
        // only be the user running or changing a filter.
        trigger: runSeqRef.current === 1 ? 'open_auto' : 'user_run',
      })
      return undefined
    }

    // running: true -> false, i.e. terminal.
    if (!runCountedRef.current) return undefined
    runCountedRef.current = false

    const startedAt = runStartedAtRef.current
    const terminal = {
      reportId,
      reportName: analyticsName ?? name,
      reportSurface: 'specialized',
      runMode: specializedJobsOn() ? 'job' : 'sync',
      handoff: false,
      trigger: runSeqRef.current === 1 ? 'open_auto' : 'user_run',
      durationMs: startedAt ? Date.now() - startedAt : undefined,
      rowCount: rowCountProp,
    }

    if (error) {
      // The message never leaves the browser — only the token. Classified from
      // the RAW prop, not the sanitized display copy, so the shape survives.
      const failure = { ...terminal, errorClass: errorClass(error) }
      if (specializedJobsOn()) trackResultRendered({ ...failure, outcome: 'failed' })
      else trackRunFailed(failure)
      return undefined
    }

    // Same split as the generic path: cb-api owns a job's completion, so on the
    // job path the browser reports only what it RENDERED.
    if (specializedJobsOn()) trackResultRendered({ ...terminal, outcome: 'rendered' })
    else trackRunCompleted(terminal)
    return undefined
    // `error` is read at the transition, deliberately not a trigger for it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running])

  /*
   * ── R3 (2026-08-28) — DOCUMENT PROVENANCE on the 21 specialized reports ────
   *
   * `filtersSummary` and `printSubtitle` reach this shell as a `useMemo` over
   * the page's LIVE filter state, so every surface that leaves the screen —
   * the client CSV/XLSX "Filters —" header, the client PDF masthead, this
   * component's own print masthead and the AI scope line — described what the
   * PANEL says rather than what the rows on screen were RUN with. Those two come
   * apart the moment a filter moves without a run, which "Reset filters" now
   * does deliberately and an ordinary edit does silently. Four of these 21 pages
   * are Balance Sheet, Trial Balance, General Ledger and P&L: a document that
   * misstates the period its figures cover is a WRONG DOCUMENT, not a cosmetic
   * defect.
   *
   * The generic engine (119 reports) fixed exactly this by binding the run's
   * filters to the result it publishes — `runResult.filterValues`, written by
   * both `setRunResult` producers, read by `documentFiltersSummary`. The SHAPE
   * is copied here, not the code, because the hosts differ where it matters:
   * ReportView owns the fetch, this shell owns neither the fetch nor the
   * filters.
   *
   * SUPERSEDED BY SPEC 131 (2026-09-30) — how the binding is made. This used to
   * read: "The only run seam it has is the `running` prop … So the equivalent
   * binding is: SNAPSHOT the page's summary when a run STARTS, PUBLISH that
   * snapshot when the run lands" — an effect here watching `running` flip. That
   * observer was only correct while runs never overlapped, and they do: with two
   * runs in flight it snapshotted whichever started after the last false edge and
   * published it over whichever run LANDED, so the documents could name a third
   * run's criteria (measured on P&L 35: rows cash 03/04–10/28, header cash
   * 03/04–11/30, panel accrual 03/04–10/28). The shell cannot tell overlapping
   * runs apart from a `running` boolean, so the binding moved to the only layer
   * that can: each page takes a ticket per run (`useLatestRun`), captures the
   * criteria at the run's START, and publishes them as `ranCriteria` in the SAME
   * commit as that run's rows. The three rules below are unchanged — only who
   * enforces them moved:
   *
   *  - Criteria are taken at the START, because that describes the filters the
   *    fetch was issued with. Re-reading the props at the terminal instead would
   *    read a panel the user may have moved while the run was in flight — the
   *    same trap ReportView avoids by passing `values` into `runReportWith`
   *    rather than closing over `filterValues`.
   *  - Published only on a SUCCESSFUL commit of the NEWEST run. This shell renders
   *    `{children}` whenever `!running`, error or not, so a failed run leaves the
   *    PREVIOUS rows on screen; adopting the failed run's filters would relabel
   *    figures it never produced. ReportView has the same rule for the same
   *    reason — only its success path calls `setRunResult`.
   *  - Before the first commit there is nothing on screen to mislabel, so both
   *    fall back to the live props. The sentinel is the OBJECT `ranCriteria`, not
   *    the string: `describeFilters` legitimately returns `''` for "no criteria"
   *    (reportFilterSummary.js), so a `??` on the string would fall back to the
   *    panel on precisely the reports that filter by nothing.
   *
   * NOT changed, deliberately: the SERVER export. A page's own `download` /
   * `serverExport` re-runs server-side against whatever the page currently
   * holds, is authoritative and stays the default; `filtersSummary` never
   * reaches it, because ReportExport reads that prop only on its client-built
   * branches (handleCsv / handleExcel / handlePdf).
   *
   * DISCHARGED 2026-09-01 (CC-53363) — this note used to read "KNOWN AND NOT
   * FIXED HERE", and the thing it described is now handled a few lines down.
   * Preserved rather than deleted, because the mechanism it names is still true
   * and is the reason the third source below exists:
   *
   *   a `?jobId=` reopen from the "My Reports" tray runs this same cycle while the
   *   page's filters are still its DEFAULTS, because the job's saved params are
   *   never seeded back into page state on a specialized page (the generic engine
   *   fixed its own version of that as CC-53051). The snapshot is then the
   *   defaults — exactly what the live prop already showed.
   *
   * So the R3 cycle was never the defect and still is not: it faithfully freezes
   * whatever the page held when the run started, and on a reopen that is the
   * defaults. What changed is that a reopen no longer lets EITHER source describe
   * the document. See `documentFiltersSummary`.
   *
   * (Spec 131: "the R3 cycle" is now the page's `useLatestRun` — the paragraph
   * above holds for it word for word.)
   */
  const ranDocMeta = ranCriteria ?? null

  /*
   * Read by every document surface below. THREE sources now, in precedence order
   * (CC-53363 added the first).
   *
   * THE DEFECT THIS CLOSES. Reopen a saved specialized run from the "My Reports"
   * tray and the page renders the JOB's figures under criteria built from the
   * PAGE's defaults — because no specialized page seeds the job's saved params
   * back into its filter state. Six surfaces carried that false line: the client
   * CSV header, the client XLSX header, ReportExport's CSV/XLSX/PDF filters line,
   * the print masthead subtitle, and the AI scope line. On a Balance Sheet that is
   * a wrong DOCUMENT: October's figures under a masthead saying "as of today", and
   * a client-facing CSV whose "Filters —" header describes criteria the rows were
   * never run under. The financial-app rule applies literally — when in doubt, do
   * not compute, surface.
   *
   * WHY THE FALLBACK IS A SENTENCE AND NOT AN OMISSION. Jordan's call, 2026-09-01.
   * Dropping the line looked tidier and is worse: an export header with no filters
   * line reads as "no filters were applied", which is itself a false claim about a
   * scoped financial report. A reader who is told the criteria were not recorded
   * knows to go and look; a reader shown nothing does not know there was anything
   * to look for.
   *
   * `savedAsOf` is the reopen signal and it is exact: all 20 pages set it from the
   * job in their `?jobId=` branch and set it to null on a fresh run, and `jobRef`
   * is consume-once, so a run after a reopen clears it. Verified across all 20.
   *
   * Spec 131 (decision 3): "on a fresh run" now means when that run COMMITS, not
   * when it starts. The saved snapshot's rows stay on screen while a fresh run is
   * in flight and after one fails, so the banner — and this sentinel — must stay
   * with them until other rows actually replace them.
   */
  const isSavedRun = savedAsOf != null
  const documentFiltersSummary = savedRunFiltersSummary
    ? savedRunFiltersSummary
    : isSavedRun
      ? SAVED_RUN_CRITERIA_UNKNOWN
      : (ranDocMeta ? ranDocMeta.filtersSummary : filtersSummary)
  /*
   * The print masthead's extra line (AR's "As of …") is filter-derived too, so on a
   * reopen it is wrong in the same way — and worse, it is the one surface where the
   * screen's saved-result banner cannot help: `ReportBand` is `print:hidden`, so a
   * printed snapshot carried NO indication it was one. So a saved run replaces it
   * with its own provenance, which is both honest and the thing a reader of the
   * paper actually needs.
   */
  const documentPrintSubtitle = isSavedRun
    ? `Saved result — as of ${fmtDisplayDateTime(new Date(savedAsOf))}`
    : (ranDocMeta ? ranDocMeta.printSubtitle : printSubtitle)

  // CC-49594: client-built PDF. The grid inside (CsvReportGrid) publishes its
  // export view through this context; the PDF button appears once it does.
  const exportViewRef = useRef(null)
  const [hasExportView, setHasExportView] = useState(false)
  // CC-49843: a grouped grid reports its own group-ness through onPublish, so
  // the "PDF: summary only" scope becomes reachable on specialized pages. The
  // wrapper components call onPublish() with no argument (they only announce
  // that a view exists), so the flag is only touched when a caller actually
  // sends it — an argument-less publish must never reset it to false.
  const [hasGroups, setHasGroups] = useState(false)
  /*
   * spec 092 — "has a result landed?", for the run control's two states.
   *
   * NOT `ready`: that prop DEFAULTS TO TRUE (it means "this page has nothing to
   * hide, show the toolbar"), so keying on it made every specialized report open
   * saying "Refresh" before it had ever run. Positively-evidenced instead — a grid
   * has published a view, or the page reported rows. A page that publishes neither
   * simply keeps saying "Run report", which is the safe direction to be wrong in:
   * it never claims a result exists when none does.
   */
  /*
   * "Rename columns" for the grid's result strip, left of Display View.
   *
   * It cannot be a prop: this shell renders the grid as `{children}`, so the
   * PAGE constructs it and the shell has nothing to pass a prop to. Publishing it
   * on the context the grid already reads is the same answer `hasRun` uses, for
   * the same reason.
   *
   * Gated exactly as before — an admin whose alias read SAID canManage, and only
   * once the grid has published columns to rename. A report that has not run has
   * none, and offering a rename against an empty list is the empty-vs-broken
   * confusion of CC-50275.
   */
  /*
   * The AI trigger, in Bar 1 — the same slot, label, icon and classes the generic
   * engine uses (ReportView.jsx:938-949).
   *
   * Before this, the AI summary appeared THREE different ways across the 140
   * reports: a collapsible Bar 1 button on the 119 generic ones, an always-on
   * inline panel on the 8 specialized pages that happened to pass `summaryData`,
   * and nothing at all on the other 12 — Aged A/R among them. Same feature, three
   * behaviours, which is exactly the non-uniformity this refresh exists to remove.
   *
   * Gated on `ready` rather than on a page passing data, so it appears once there
   * is a result to summarise and on every page equally.
   */
  const askTrigger = AI_REPORT_SUMMARY && aiMode !== 'off' && ready && !running && !error ? (
    <button
      type="button"
      onClick={() => setAiCollapsed((c) => !c)}
      aria-expanded={!aiCollapsed}
      aria-controls={AI_PANEL_ID}
      className="btn-rc btn-rc-ai"
    >
      <span aria-hidden="true">&#10024;</span>
      {aiMode === 'chat' ? 'Ask' : 'AI Summary'}
    </button>
  ) : null

  const stripActions = useMemo(
    () => (ready && canRenameColumns ? (
      <button
        type="button"
        onClick={() => setRenamingColumns(true)}
        /* B3 — `h-[30px] text-[13px]` deleted, not "changed": they were DEAD
           CODE. .btn-rc is an unlayered rule and Tailwind v4 emits utilities
           inside `@layer utilities`, where an unlayered rule wins regardless of
           specificity — so this button has always rendered at .btn-rc's 38px /
           14px and those two utilities only told the next reader a lie about
           what it does. Nothing about the rendered control changes here. */
        className="btn-rc"
      >
        Rename columns
      </button>
    ) : null),
    [ready, canRenameColumns]
  )
  const runState = useMemo(
    () => ({
      hasRun: (hasExportView || rowCount > 0) && !running && !error,
      stripActions,
      reportName: name,
      // Spec 131 / E4: read by SpecializedFilterBar (useReportRunKey) so a Reset
      // issued mid-run clears its stale band once the reset's own run STARTS.
      runKey,
    }),
    [hasExportView, rowCount, running, error, stripActions, name, runKey]
  )
  const exportCtx = useMemo(
    () => ({
      ref: exportViewRef,
      onPublish: (meta) => {
        setHasExportView(true)
        if (typeof meta?.hasGroups === 'boolean') setHasGroups(meta.hasGroups)
      },
    }),
    []
  )
  // CC-49844: PDF is no longer built here — ReportExport owns every export
  // format now, so the scope checkboxes apply to PDF on a specialized report
  // exactly as they do on a generic one (they previously did not: this shell
  // hard-coded honorColumns/honorFilters = true).

  // CC-49908: pages with no server export (e.g. Accounts Payable summary) get
  // CSV/Excel built CLIENT-side from the same published grid view the PDF
  // uses — real numbers for numeric columns, SUM-only totals row (the
  // exportClientView guardrails). A page-provided `download` stays
  // authoritative and is preferred when present.
  const onClientExport = async (format) => {
    const view = exportViewRef.current?.()
    if (!view) return
    // Spec 157: the third client-build path honours a page's refusal too
    // (clientExportRefusal.js), so no page can be refused by one and not another.
    const refused = clientExportRefusal(view, format)
    if (refused) {
      alert(`Export failed: ${refused}`)
      return
    }
    try {
      const scope = { honorColumns: true, honorFilters: true }
      if (format === 'csv') {
        const { exportClientCsv } = await import('./exportClientView')
        // CC-50029: same audit header block as the Excel branch below.
        exportClientCsv(view, scope, exportFileName(name, 'csv'), {
          title: name,
          // The RUN's criteria, not the panel's. This whole branch builds the
          // file from `exportViewRef` — the view the GRID published, i.e. the
          // rows already on screen — so the header has to name the run those
          // rows came from or the file contradicts itself.
          filtersLine: documentFiltersSummary ? `Filters — ${documentFiltersSummary}` : undefined,
          generatedLine: `Generated ${fmtDisplayDateTime(new Date())}`,
        })
      } else {
        const { exportClientExcel } = await import('./exportClientView')
        await exportClientExcel(view, scope, exportFileName(name, 'xlsx'), {
          title: name,
          filtersLine: documentFiltersSummary ? `Filters — ${documentFiltersSummary}` : undefined,
          generatedLine: `Generated ${fmtDisplayDateTime(new Date())}`,
        })
      }
    } catch (err) {
      alert(`Export failed: ${err.message}`)
    }
  }

  const onExport = async (format) => {
    if (!download) return onClientExport(format)
    try {
      await download(format, getAccessTokenSilently)
    } catch (err) {
      alert(`Export failed: ${err.message}`)
    }
  }

  // CC-49837: report cells are injected as HTML strings, so in-app links in
  // them can't be <Link>s. Anchors marked data-cb-nav keep a real href (new
  // tab / middle-click still work) but left-clicks route through the SPA.
  // Keyboard: anchors are natively focusable and Enter fires this click.
  const onContentClick = (e) => {
    const a = e.target.closest?.('a[data-cb-nav]')
    if (!a) return
    e.preventDefault()
    navigate(a.getAttribute('href'))
  }

  /*
   * spec 092 — Bar 1. Built here rather than inline in the row below because
   * this shell is the ONLY chrome host for all 21 specialized reports, so this
   * is the one edit that gives every one of them the same page-action cluster.
   *
   * Export moves up here out of the toolbar that used to sit above the grid.
   * The designer's rule for this row: it carries things that act on the report as
   * an object, not on the data inside it — and an export acts on the whole report.
   *
   * "Rename columns" deliberately does NOT come with it. That
   * change what the export CONTAINS (AR's hide-zero / hide-inactive) or how the
   * result is drawn, which is the grid's tier, not the page's. They stay where
   * they are until Bar 3 and the Display View panel exist (Pieces 5 / 5a).
   */
  const exportControl = ready ? (
    <ReportExport
      reportName={name}
      analyticsName={analyticsName}
      reportSummary={summary}
      /* The RUN's criteria, not the panel's — see documentFiltersSummary. Read
         only by ReportExport's client-built CSV/XLSX/PDF branches; the page's
         own server download re-runs and stamps its own header. */
      filtersSummary={documentFiltersSummary}
      firmName={firmName}
      rowCount={rowCount}
      hasGroups={hasGroupsProp ?? hasGroups}
      /* No server download and no published grid view = nothing to export yet
         (GL Budget before a budget is chosen): omit the format buttons, exactly
         as this shell did before. */
      exportFormats={download || hasExportView || getView ? exportFormats : []}
      serverExport={download || hasExportView ? onExport : undefined}
      /* spec 094 — one Report Export event is emitted inside ReportExport for
         the whole menu; this only tells it which of the 21 specialized pages
         it is on, so exports are attributable without a second event. */
      surface="specialized"
      getView={getView || (hasExportView ? () => exportViewRef.current?.() : undefined)}
    />
  ) : null

  const actionBar = (
    <ReportActionBar reportName={name} reportSummary={summary} onBack={goBack} ask={askTrigger}>{exportControl}</ReportActionBar>
  )

  return (
    /* Full width, matching ReportView. The 21 specialized reports were capped at
       max-w-[1400px] and centred while the 119 generic ones were w-full, so the
       same grid used the whole screen on one set and two-thirds of it on the
       other. Jordan spotted it comparing report 190 with report 21. */
    <div className="report-shell">
      {/* Spec 209 §3: the wrapper is a plain block (its child's bottom margin collapses
          through it), so only the class is added; other pages get the bar unwrapped. */}
      {privateName ? <div className={PENDO_IGNORE}>{actionBar}</div> : actionBar}


      {/* CC-49339/CC-49340: print/PDF header — firm + run timestamp (the h1
          above already prints the report title).
          CC-49844 round 3: "Data only" now suppresses this too. It previously
          governed only the CSV/Excel/PDF WRITERS, so a user who checked the box
          and used PRINT still got "CENTERBASE DEMO / Generated … · Printed by …"
          — byte-for-byte the three lines QA reported as a PDF failure. The
          client PDF was verified clean on the deployed build, so the print path
          is the remaining way to see that block; honoring the box on both
          surfaces removes the ambiguity rather than arguing about which button
          was pressed. */}
      {!dataOnly && (
        <div className="hidden print:block mb-8 text-center text-gray-900">
          <p className="text-xl font-bold uppercase tracking-wide">{firmName}</p>
          {/* A point-in-time statement is meaningless on paper without its as-of
              date, so a page may add one line here (CC-50013 G6). */}
          {documentPrintSubtitle && (
            /* data-testid because this <p> carries the SAME prose as SavedResultBanner
               ("Saved result - as of ..."), and the banner already has one. Two elements,
               one testid between them, meant every test had to match the PROSE - and which
               element it hit depended on getNodeText reading direct text-node children only
               plus where a <strong> happens to sit inside the banner. That cost two sessions
               a conflict-free-rebase failure each (CC-53363 follow-up). Scope to this id. */
            <p data-testid="print-masthead-subtitle" className="text-base mt-1">{documentPrintSubtitle}</p>
          )}
          <p className="text-sm text-gray-600 mt-1">
            Generated {fmtDisplayDateTime(new Date())}
            {user?.name ? ` · Printed by ${user.name}` : ''}
          </p>
        </div>
      )}

      {/* Reopened from the "My Reports" tray → the data is a saved snapshot, not a
          live run. Say so, with the run time, so the user isn't misled. */}
      <SavedResultBanner asOf={savedAsOf} />

      {/* spec 092: the run control turns from the primary "Run report" CTA into a
          neutral "Refresh" once a result exists. The page builds this bar but the
          SHELL is what knows whether a run has landed, so the signal is published
          here rather than threaded through 20 call sites. `ready` is the shell's
          own "a run has produced a result" flag (see its prop default).

          Two corrections to what this note used to say (2026-08-26):
          • It said "the orange 'Run report' CTA". B1 made the primary CTA BLUE
            (#1d4ed8 = --color-rc-chip-text, white on it 6.70:1, the same blue as
            the refresh chips and focus rings). Nothing orange is involved in the
            run control any more; the brand orange survives only on the Reports
            landing page's pillar dots and signature period.
          • It carried, by implication, spec 092's claim that this swap cannot
            reflow the bar. That claim was FALSE when written — .btn-cb-primary
            had no border and .btn-cb-neutral had a 1px one, so the two states
            measured 36px and 38px under border-box and the bar grew 2px the
            instant a report finished. index.css fixed the underlying cause by
            giving .btn-cb-primary a same-colour 1px border. The claim is true
            NOW, and it holds only because the two states share padding, font
            metrics AND border width — see the note on .btn-cb-neutral. */}
      <div data-print-hide>
        <ReportRunStateContext.Provider value={runState}>{filterBar}</ReportRunStateContext.Provider>
      </div>

      {/* CC-49993: every specialized page's error is built from err.message, so
          raw CB exception text — including server filesystem paths — used to
          land here. Sanitize once at the render point rather than in each page's
          errorMessage (since spec 131 the pages' catch blocks are one, in
          useLatestRun; the per-page message functions still differ).
          role="alert" — spec 152 §17 review: this band is where all 20 specialized
          pages (21 reports) show spec 152's reopen sentences ("This saved result is no
          longer stored…", "This run is no longer available…", via useLatestRun) and
          every other run failure. It was a bare div, so a screen reader was told nothing
          (SC 4.1.3); ReportView's band and the Queue page's are role="alert". It mounts
          WITH its text, which is when an alert is announced. Same testid as ReportView's
          band (the two never render together): tests find it by testid, because a
          page's filter bar can render alerts of its own. */}
      {error && (
        <div role="alert" data-testid="report-run-error" className={`${privateName || privateError ? `${PENDO_IGNORE} ` : ''}bg-red-50 border border-red-100 rounded-xl p-4 mb-6 text-sm text-red-700`}>
          {userFacingError(error, `${name} could not be generated. Please try again.`)}
        </div>
      )}

      {/*
        * Section 1b on the specialized path — the SAME band as the generic engine
        * (`RunningReportBand`), replacing this shell's own 12rem centred spinner
        * card. That is the whole reason the band is a component: 21 pages and 119
        * reports now render one in-flight state instead of two that drifted.
        *
        * AR's determinate row progress is carried INTO the band rather than lost
        * to the restyle — it pages its rows in and genuinely knows how far along
        * it is, and the band keeps the role/aria-value* set this bar had.
        *
        * No `handsOffAfterMs`: the 20-second hand-off does not exist on this path.
        * `runReportAsJob` gives these pages no job id, percent or detach signal,
        * so there is nothing to hand off to and the band must not promise one.
        * That gap is its own piece of work (D1-4) and is deliberately not faked
        * here — a band that says "it carries on in the background" on a page
        * where it does not is exactly the kind of plausible-but-false statement
        * this codebase keeps paying for.
        */}
      {running && (
        <RunningReportBand
          /* See ReportView's copy of this: the key re-seeds the elapsed clock.
             Spec 131: `runStartedAt` is now re-stamped per `runKey` as well (the
             effect above), so a superseding run gets its own clock too. */
          key={runStartedAt}
          /* "Running <report name>": the firm's name on a generic Advanced Report (spec 209 §3). */
          className={privateName ? `${PENDO_IGNORE} mb-6` : 'mb-6'}
          reportName={name}
          startedAt={runStartedAt}
          progress={progress}
        />
      )}

      {/* Result-shaping controls sit directly above the grid. Export used to be
          here too; spec 092 moved it up into Bar 1, because it acts on the report
          as an object while everything left in this row acts on the result. */}
      {!running && (
        <>
          {/* CC-49844 (kept for the record — the behaviour it describes is
              unchanged, only the button's POSITION moved): specialized reports
              used to render their own four plain buttons, so the export SCOPE
              options existed on the generic engine only, and QA reported them
              missing on Balance Sheet and General Ledger. Every specialized page
              still routes through the SAME ReportExport the generic path uses —
              see `exportControl` above. The page's own `download` is still handed
              over as `serverExport`, so unchecked boxes still mean that page's
              authoritative server export. */}
          {/* The floating row that used to sit here is GONE. It held a page's own
              display controls (via an `exportExtras` prop, now deleted) and then
              just "Rename columns", which has moved onto the grid's result strip
              as `stripActions` above. Do not reinstate either — a floating row of
              controls above the result is precisely what this refresh removed. */}
          {/*
              Behind the Bar 1 trigger now, instead of always-on.

              `getView` is the fallback that makes this work on all 20 pages: the
              grid publishes its export view to this shell through
              ReportExportViewContext, which is the same source ReportView feeds
              its panel from. A page that passes `summaryData` explicitly still
              wins — an explicit prop beats the ambient value, the same rule
              `hasRun` follows — but a page that passes nothing is no longer
              excluded from the feature.

              Grounding is unchanged: the payload is still built from the report's
              OWN figures via the grid's export view, never free-form.
          */}
          {AI_REPORT_SUMMARY && aiMode !== 'off' && !aiCollapsed && ready && !running && !error && (
            <div id={AI_PANEL_ID} className="mb-4">
              <ReportAiSummary
                mode={aiMode}
                data={summaryData}
                getView={summaryData ? undefined : () => exportViewRef.current?.()}
                reportName={name}
                /* The RUN's criteria. The panel's would be a false statement
                   about what the model was actually handed: the payload is
                   built from the grid's published view, i.e. the run's rows. */
                scope={aiScopeLine(documentFiltersSummary, rowCount)}
                onDismiss={() => setAiCollapsed(true)}
              />
            </div>
          )}
          {/* Delegated click handler for data-cb-nav anchors inside injected
              HTML — the anchors themselves are the interactive (and keyboard-
              focusable) elements, this div is just event plumbing. */}
          <div onClick={onContentClick}>
            {/* stripActions rides this provider and the GRID is in {children},
                so the provider wraps here as well as around the filter bar. Same
                value object on both, so no consumer can see two different ones. */}
            <ReportRunStateContext.Provider value={runState}>
              <ReportExportViewContext.Provider value={exportCtx}>{children}</ReportExportViewContext.Provider>
            </ReportRunStateContext.Provider>
          </div>

          {/* CC-50291: the same editor the generic reports use, against the
              columns the grid published. `columnId` carries the grid's stable
              key (CSV header key / row key), which is what the backend stores
              these under — NOT the positional index the grid uses internally
              to pair columns with cells. */}
          {renamingColumns && (
            <ColumnAliasEditor
              reportKey={aliasCtx.reportKey}
              reportName={name}
              columns={aliasCtx.columns.map((c) => ({ columnId: c.key, label: c.label }))}
              onClose={() => setRenamingColumns(false)}
            />
          )}
        </>
      )}
    </div>
  )
}

export default SpecializedReportShell
